import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { SplaBinding, SplaCandidate } from '../types/models/Spla';
import type { ApiResponseSplaBindingList, ApiResponseSplaCandidateList } from '../types/api/ApiResponseSpla';

// Staff-only SPLA bindings + agent-discovery candidates hooks (the compute layer). Both follow the
// useAdminProducts(enabled) deferred-fetch shape. Branch on the JSend `body.status`, NEVER
// `response.ok` — the backend maps controller exceptions to `status: 'error'` at HTTP 200.

interface UseAdminSplaBindingsReturn {
  bindings: SplaBinding[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /** Replace one binding in place (matched by `id`) from a PATCH response. */
  applyBinding: (updated: SplaBinding) => void;
  /** Drop one binding after a DELETE. */
  removeBinding: (id: number) => void;
}

// GET /admin/spla/bindings[?client_id=N] — the license ↔ subject attachments that auto-maintain
// `computed` consumption rows. `refreshKey` bumps force a refetch (e.g. after a binding is created
// from the Candidates tab, or a recompute refreshes every row's health columns).
export const useAdminSplaBindings = (
  enabled: boolean,
  clientId: number | null,
  refreshKey = 0,
): UseAdminSplaBindingsReturn => {
  const [bindings, setBindings] = useState<SplaBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchBindings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = clientId !== null ? `?client_id=${clientId}` : '';
      const response = await apiFetch(`/admin/spla/bindings${query}`);
      const body = (await response.json().catch(() => null)) as ApiResponseSplaBindingList | null;
      if (body?.status === 'success' && body.data?.bindings) {
        setBindings(body.data.bindings);
      } else {
        setError(body?.message ?? 'Failed to load the SPLA bindings');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the SPLA bindings');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, clientId]);

  const applyBinding = useCallback((updated: SplaBinding) => {
    setBindings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }, []);

  const removeBinding = useCallback((id: number) => {
    setBindings((prev) => prev.filter((b) => b.id !== id));
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchBindings();
    }
    // refreshKey re-runs the fetch when the Candidates tab creates a binding.
  }, [enabled, auth.isAuthenticated, fetchBindings, refreshKey]);

  return { bindings, loading, error, refetch: fetchBindings, applyBinding, removeBinding };
};

export interface AdminSplaCandidateFilters {
  /** `?unbound=1` — hide subjects that already have a binding (any license). */
  unboundOnly: boolean;
  /** `?dismissed=1` — include dismissed rows (audit). */
  includeDismissed: boolean;
}

interface UseAdminSplaCandidatesReturn {
  candidates: SplaCandidate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// GET /admin/spla/bindings/candidates — the agent discovery queue (what the manager-agent saw
// running, awaiting a staff attribution decision). Advisory only: the agent never writes
// consumption or bindings. Default list: not-dismissed rows, newest report first. `refreshKey`
// bumps force a refetch (a binding created from the queue changes `binding_count` / `?unbound=1`).
export const useAdminSplaCandidates = (
  enabled: boolean,
  filters: AdminSplaCandidateFilters,
  refreshKey = 0,
): UseAdminSplaCandidatesReturn => {
  const [candidates, setCandidates] = useState<SplaCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const { unboundOnly, includeDismissed } = filters;

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (unboundOnly) {
        params.set('unbound', '1');
      }
      if (includeDismissed) {
        params.set('dismissed', '1');
      }
      const query = params.toString();
      const response = await apiFetch(`/admin/spla/bindings/candidates${query ? `?${query}` : ''}`);
      const body = (await response.json().catch(() => null)) as ApiResponseSplaCandidateList | null;
      if (body?.status === 'success' && body.data?.candidates) {
        setCandidates(body.data.candidates);
      } else {
        setError(body?.message ?? 'Failed to load the discovery queue');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the discovery queue');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, unboundOnly, includeDismissed]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchCandidates();
    }
    // refreshKey re-runs the fetch when a binding created from the queue changes the rows.
  }, [enabled, auth.isAuthenticated, fetchCandidates, refreshKey]);

  return { candidates, loading, error, refetch: fetchCandidates };
};
